import * as React from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import { IsMobileContext } from '../../common/Context'
import { FormState, ActionTypes, FormActionTypes } from './types'
import Consent from '../CheckboxWithAccordionItem'
import Input from '../Input'
import TextArea from '../TextArea'
import Dropdown from '../Dropdown'
import Button from '../Button'
import { MailingService } from '../../common/MailingService'
import { Form as FormProps } from '../FormSection/model'
import './style.scss'

const Form: React.FC<FormProps> = ({
  options,
  nameFieldTitle,
  // companyFieldTitle,
  emailFieldTitle,
  phoneFieldTitle,
  messageFieldTitle,
  saveButtonTitle,
  consentDesktopFirstLine,
  consentDesktopRestLines,
  consentMobileFirstLine,
  consentMobileRestLines,
  ReCAPTCHAErrorMessage,
  successMessage,
  errorMessage,
}) => {
  const { isMobile } = React.useContext(IsMobileContext)

  const formInitialState = {
    values: {
      topic: '',
      name: '',
      company: '',
      email: '',
      phone: '',
      message: '',
      consent: false,
    },
    validities: {
      topic: false,
      name: false,
      company: true, //false,
      email: false,
      phone: true,
      message: false,
      consent: false,
    },
    submitTouched: false,
    formIsValid: false,
  }

  const formReducer = (state: FormState, action: FormActionTypes) => {
    switch (action.type) {
    case ActionTypes.FORM_UPDATE: {
      const updatedValues = {
        ...state.values,
        [action.fieldId]: action.value,
      }
      const updatedValidities = {
        ...state.validities,
        [action.fieldId]: action.isValid,
      }
      let updatedFormIsValid = true
      for (const key in updatedValidities) {
        updatedFormIsValid = updatedFormIsValid && updatedValidities[key]
      }
      return {
        ...state,
        values: updatedValues,
        validities: updatedValidities,
        formIsValid: updatedFormIsValid,
      }
    }
    case ActionTypes.FORM_SUBMIT:
      return {
        ...state,
        submitTouched: true,
      }
    default:
      return state
    }
  }

  const [showReCAPTCHA, setShowReCAPTCHA] = React.useState(false)
  const [isReCAPTCHAError, setIsReCAPTCHAError] = React.useState(false)

  const [formState, dispatch] = React.useReducer(formReducer, formInitialState)
  const [error, setError] = React.useState<null | boolean>(null)

  const [country, setCountry] = React.useState('PL')
  const [clientIP, setClientIP] = React.useState('')

  const ReCAPTCHARef = React.useRef<ReCAPTCHA>(null)

  React.useEffect(() => {    
    if (process.env.GATSBY_GEOLOCATION_API) {
      fetch(process.env.GATSBY_GEOLOCATION_API)
        .then(res => res.json())  
        .then(data => setClientIP(data.ip))
    }
  }, [])

  React.useEffect(() => {
    checkReCAPTCHA()
  }, [clientIP])

  const checkReCAPTCHA = (): void => {
    MailingService.detectPotentialSpam({clientIP, action: 'check'}).then(res => setShowReCAPTCHA(res || false))
  }

  const fieldChangeHandler = React.useCallback((fieldId, value, isValid) => {
    dispatch({
      type: ActionTypes.FORM_UPDATE,
      fieldId,
      value,
      isValid
    })
  }, [dispatch])

  const triggerGTM = () => {
    if (typeof dataLayer !== 'undefined' && Array.isArray(dataLayer)) {
      dataLayer.push({'event': 'submit-button-click'})
    }
  }

  const submitHandler = async () => {
    setError(null)
    dispatch({
      type: ActionTypes.FORM_SUBMIT,
    })
    if (formState.formIsValid) {
      if (process.env.GATSBY_GEOLOCATION_API) {
        fetch(process.env.GATSBY_GEOLOCATION_API)
          .then(res => res.json())  
          .then(data => setCountry(data.country))
      }
      try {
        if (showReCAPTCHA) {
          const token = ReCAPTCHARef.current?.getValue()
          if (!token) {
            setIsReCAPTCHAError(true)
            return
          }
          setIsReCAPTCHAError(false)
          MailingService.detectPotentialSpam({clientIP, action: 'submit', token}).then(res => setShowReCAPTCHA(res || false))
        } else {
          MailingService.detectPotentialSpam({clientIP, action: 'submit'}).then(res => setShowReCAPTCHA(res || false))
        }
        triggerGTM()

        const res = await MailingService.sendContactForm({
          topic: formState.values.topic,
          name: formState.values.name,
          company: formState.values.company,
          email: formState.values.email,
          phone: formState.values.phone,
          message: formState.values.message,
          country: country,
          source: document.referrer,
          url: location.href,
          referrer: history.state?.prevPath ? location.origin + history.state.prevPath : 'no previous URL'
        })
        res ? setError(false) : setError(true)
      } catch (err) {
        setError(true)
        console.log('Mailing service error:', err) //TODO: handle error
      }
    }
  }

  const consentfirstLine = isMobile ? consentMobileFirstLine : consentDesktopFirstLine
  const consentRestLines = isMobile ? consentMobileRestLines : consentDesktopRestLines
  const optionsArray = options?.split(',').map(option => option.trim()) || ['']

  return (
    <div className="Form grid grid-cols-6 gap-x-m">
      <Dropdown
        field="topic"
        options={optionsArray}
        className="mb-mobile-xs lg:mb-xs col-span-6"
        required
        onChange={fieldChangeHandler}
        revalidate={formState.submitTouched}
      />
      <Input
        field="name"
        placeholder={nameFieldTitle}
        className="mb-mobile-xs lg:mb-xs col-span-6"
        required
        onChange={fieldChangeHandler}
        revalidate={formState.submitTouched}
      />
      {/* <Input
        field="company"
        placeholder={companyFieldTitle}
        className="mb-mobile-xs lg:mb-xs col-span-6"
        required
        onChange={fieldChangeHandler}
        revalidate={formState.submitTouched}
      /> */}
      <Input
        field="email"
        placeholder={emailFieldTitle}
        className="mb-mobile-xs lg:mb-xs col-span-6 lg:col-span-3"
        required
        email
        onChange={fieldChangeHandler}
        revalidate={formState.submitTouched}
      />
      <Input
        field="phone"
        placeholder={phoneFieldTitle}
        className="mb-mobile-xs lg:mb-xs col-span-6 lg:col-span-3"
        phone
        onChange={fieldChangeHandler}
        revalidate={formState.submitTouched}
      />
      <TextArea
        field="message"
        placeholder={messageFieldTitle}
        className="mb-mobile-xs lg:mb-xs col-span-6"
        required
        onChange={fieldChangeHandler}
        revalidate={formState.submitTouched}
      />
      <div className="pb-mobile-xxxs lg:pb-xxxs border-b border-separator border-opacity-separator col-span-6"></div>
      <div className="pt-mobile-xxxs lg:pt-xxxs col-span-6"></div>
      <Consent
        field="consent"
        className="col-span-6"
        labelFirstLine={consentfirstLine}
        labelRestLines={consentRestLines}
        required
        onChange={fieldChangeHandler}
        revalidate={formState.submitTouched}
      />
      <div className="pb-mobile-xxxs lg:pb-xxxs border-b border-separator border-opacity-separator col-span-6"></div>
      {showReCAPTCHA && process.env.GATSBY_RECAPTCHA_SITE_KEY && (
        <div className="pt-mobile-xxxs lg:pt-xxxs mb-mobile-xs lg:mb-xs col-span-6">
          <ReCAPTCHA 
            sitekey={process.env.GATSBY_RECAPTCHA_SITE_KEY}
            ref={ReCAPTCHARef}
          />
          {isReCAPTCHAError && <p className='FontS text-accent'>{ReCAPTCHAErrorMessage}</p>}
        </div>
      )}
      <div className="pt-mobile-xxxs lg:pt-xxxs mb-mobile-xs lg:mb-xs col-span-6"></div>
      {error !== null && (
        <div className={`FontM col-span-6 mb-mobile-s lg:mb-s
          ${isMobile ? 'text-left' : 'text-right'}
          ${error ? 'text-accent' : 'text-accent2'}`}
        >
          {error ? errorMessage : successMessage}
        </div>
      )}
      <div className="col-span-6">
        <Button
          title={saveButtonTitle}
          className="ButtonPrimary FontS block ml-auto mr-0"
          onClick={submitHandler}
          disabled={error === false}
        />
      </div>
    </div>
  )
}

export default Form
